import React, { useEffect, useRef, useState } from "react";
import "./LiveKitHome.css";

/**
 * Join page for LiveKit session — similar to Smart-interview-ui Home.jsx.
 * Parent (LiveKitSessionPage) can pre-fill roomId and nickname from URL query params.
 */
const LiveKitHome = ({
  roomId,
  setRoomId,
  nickname,
  setNickname,
  onJoin,
  audioEnabled,
  setAudioEnabled,
  videoEnabled,
  setVideoEnabled,
  isJoining = false,
}) => {
  const videoRef = useRef(null);
  const [mediaHint, setMediaHint] = useState(null);

  useEffect(() => {
    let stream = null;
    const initLocalStream = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setMediaHint(
            "This browser does not support camera/mic preview. On iPhone or iPad use Safari and HTTPS, then join the meeting."
          );
          return () => {};
        }
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            facingMode: { ideal: "user" },
            width: { ideal: 640, max: 1280 },
            height: { ideal: 480, max: 720 },
            frameRate: { ideal: 24, max: 30 },
          },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setMediaHint(null);
      } catch (err) {
        console.error("Error accessing media devices:", err);
        setMediaHint(
          "Could not access camera or microphone for preview. Allow permissions in browser settings (Safari on iOS: Settings → Safari → Camera/Microphone). You can still join and enable devices in the meeting."
        );
      }
      return () => {
        stream?.getTracks().forEach((track) => track.stop());
      };
    };
    let cleanup;
    initLocalStream().then((fn) => {
      cleanup = fn;
    });
    return () => {
      cleanup?.();
    };
  }, []);

  const toggleAudio = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (videoRef.current?.srcObject) {
      const videoTrack = videoRef.current.srcObject.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (roomId?.trim() && nickname?.trim()) {
      onJoin();
    }
  };

  const canJoin = roomId?.trim() && nickname?.trim();

  return (
    <div className="livekit-home">
      <div className="livekit-home__card">
        <h1 className="livekit-home__title">Join LiveKit Meeting</h1>
        <p className="livekit-home__subtitle">
          Enter room details and check your camera and microphone before joining.
        </p>

        <form onSubmit={handleSubmit} className="livekit-home__form">
          {mediaHint && (
            <p className="livekit-home__media-hint" role="status">
              {mediaHint}
            </p>
          )}
          <div className="livekit-home__fields">
            <div className="livekit-home__field">
              <label htmlFor="livekit-roomid">Room ID</label>
              <input
                id="livekit-roomid"
                type="text"
                value={roomId}
                onChange={(e) =>
                  setRoomId(e.target.value.trim().toUpperCase())
                }
                placeholder="e.g. INTERVIEW-001"
                autoComplete="off"
                disabled={isJoining}
              />
            </div>
            <div className="livekit-home__field">
              <label htmlFor="livekit-name">Your name</label>
              <input
                id="livekit-name"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value.trim())}
                placeholder="Enter your name"
                autoComplete="name"
                disabled={isJoining}
              />
            </div>
          </div>

          <div className="livekit-home__preview">
            <div className="livekit-home__video-wrap">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="livekit-home__video"
              />
              {!videoEnabled && (
                <div className="livekit-home__video-placeholder">
                  Camera off
                </div>
              )}
            </div>
            <div className="livekit-home__controls">
              <button
                type="button"
                onClick={toggleAudio}
                className={`livekit-home__btn livekit-home__btn--control ${
                  audioEnabled
                    ? "livekit-home__btn--on"
                    : "livekit-home__btn--off"
                }`}
                disabled={isJoining}
              >
                {audioEnabled ? "Mute" : "Unmute"}
              </button>
              <button
                type="button"
                onClick={toggleVideo}
                className={`livekit-home__btn livekit-home__btn--control ${
                  videoEnabled
                    ? "livekit-home__btn--on"
                    : "livekit-home__btn--off"
                }`}
                disabled={isJoining}
              >
                {videoEnabled ? "Stop video" : "Start video"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="livekit-home__btn livekit-home__btn--join"
            disabled={!canJoin || isJoining}
          >
            {isJoining ? "Joining…" : "Join meeting"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LiveKitHome;
